/**
 * src/components/Financial/modules/ApogeePerigeeModule.jsx
 * 【完全修正版：ダークモード対応・デザイン統一・フチ取り削除】
 */
import React from "react";
import { Target } from "lucide-react";
import * as Astro from "../../../astronomy.js";

/**
 * 月の遠地点・近地点の計算ロジック
 */
export const calculateApogeePerigee = function (timeline) {
  var hits = [];
  if (!timeline || timeline.length < 3) return hits;

  var getDate = function (row) {
    return row.timestamp ? new Date(row.timestamp) : new Date(row.Date);
  };

  // 天体IDの取得
  var bodyMoon =
    Astro.Body && Astro.Body.Moon !== undefined ? Astro.Body.Moon : 11;

  for (var i = 1; i < timeline.length - 1; i++) {
    var d0 = getMoonDist(getDate(timeline[i - 1]), bodyMoon);
    var d1 = getMoonDist(getDate(timeline[i]), bodyMoon);
    var d2 = getMoonDist(getDate(timeline[i + 1]), bodyMoon);

    if (d0 === 0 || d1 === 0 || d2 === 0) continue;

    var slope1 = d1 - d0;
    var slope2 = d2 - d1;

    // 極値判定
    if (slope1 < 0 && slope2 > 0) {
      var phase = getMoonPhase(getDate(timeline[i]));
      var label = "Perigee";
      var symbol = "P";
      var color = "#8e44ad";

      // スーパームーン（近地点満月）判定
      if (Math.abs(phase - 180) < 15) {
        label = "スーパームーン (近地点満月)";
        symbol = "SM";
        color = "#f1c40f";
      }
      hits.push({ index: i, symbol: symbol, label: label, color: color });
    } else if (slope1 > 0 && slope2 < 0) {
      var phase = getMoonPhase(getDate(timeline[i]));
      var label = "Apogee";
      var symbol = "A";
      var color = "#2980b9";

      // 遠地点新月判定
      if (phase < 15 || phase > 345) {
        label = "遠地点新月";
        symbol = "MN";
        color = "#34495e";
      }
      hits.push({ index: i, symbol: symbol, label: label, color: color });
    }
  }
  return hits;
};

/**
 * 月の距離計算（天文計算ライブラリ使用）
 */
function getMoonDist(date, bodyMoon) {
  try {
    var time = Astro.MakeTime ? Astro.MakeTime(date) : date;
    if (Astro.EclipticGeoMoon) {
      return Astro.EclipticGeoMoon(time).dist;
    }
    if (Astro.GeoVector) {
      var v = Astro.GeoVector(bodyMoon, time, true);
      return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
    }
    return 0;
  } catch (e) {
    return 0;
  }
}

/**
 * 月相の計算（太陽と月の離角）
 */
function getMoonPhase(date) {
  try {
    var m = Astro.getPlanetLongitude("Moon", date);
    var s = Astro.getPlanetLongitude("Sun", date);
    return (m - s + 360) % 360;
  } catch (e) {
    return 180;
  }
}

/**
 * 描画レイヤー (ダークモード対応・フチ取り削除版)
 */
export const ApogeePerigeeLayer = function (props) {
  var { hits, displayRange, coords, isDarkMode } = props;
  if (!hits || hits.length === 0) return null;

  return (
    <g className="apogee-perigee-layer">
      {hits.map(function (hit, i) {
        if (
          hit.index < displayRange.start ||
          hit.index >= displayRange.start + displayRange.count
        )
          return null;
        var xPos = coords.getX(hit.index - displayRange.start);

        return (
          <g key={"ap-" + i}>
            <line
              x1={xPos}
              y1={coords.pad.t}
              x2={xPos}
              y2={coords.pad.t + coords.iH}
              stroke={hit.color}
              strokeWidth={hit.symbol === "SM" ? "2" : "1"}
              strokeDasharray="4,4"
              opacity="0.6"
            />
            <g
              transform={"translate(" + xPos + ", " + (coords.pad.t - 20) + ")"}
            >
              {/* アイコンの白い縁取り(stroke)を削除 */}
              <circle
                r={hit.symbol === "SM" ? "10" : "8"}
                fill={hit.color}
                opacity="0.9"
              />
              <text
                dy=".35em"
                fill="#fff"
                fontSize={hit.symbol === "SM" ? "9" : "10"}
                textAnchor="middle"
                fontWeight="bold"
              >
                {hit.symbol}
              </text>
              {(hit.symbol === "SM" || hit.symbol === "MN") && (
                <text
                  y="-15"
                  fill={hit.color}
                  fontSize="9"
                  textAnchor="middle"
                  fontWeight="bold"
                >
                  {hit.label}
                </text>
              )}
            </g>
          </g>
        );
      })}
    </g>
  );
};

/**
 * 設定UI (ダークモード対応・デザイン統一版)
 */
export const ApogeePerigeeSettingsUI = function (props) {
  var show = props.show;
  var setShow = props.setShow;
  var isDarkMode = props.isDarkMode;

  // テーマ定義の統一
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
  };

  /**
   * ボタン共通スタイル (f98.jpg に合わせた統一デザイン)
   */
  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: theme.text,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Target size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />{" "}
          <span>月の遠地点・近地点 (Apogee & Perigee)</span>
        </div>
        <button
          onClick={function () {
            setShow(!show);
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>
      <div
        style={{
          marginTop: "12px",
          fontSize: "12px",
          color: theme.subText,
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        ※
        月の軌道上で地球から最も遠い点(Apogee)と最も近い点(Perigee)を検出します。
      </div>
    </div>
  );
};
